import React, { useEffect, useMemo, useState } from 'react';
import {
  HELP_ARTICLES,
  HELP_SECTIONS,
  getHelpArticlesForTab,
  getSectionTitle,
  searchHelpArticles,
} from '../data/helpArticles';
import HelpAssistant from './HelpAssistant';

type HelpDrawerView = 'assistant' | 'articles';

export default function HelpDrawer({
  activeTab,
  activeTabLabel,
  onClose,
  onOpenSupport,
  open,
}: {
  activeTab: string;
  activeTabLabel?: string;
  onClose: () => void;
  onOpenSupport: () => void;
  open: boolean;
}) {
  const contextArticles = useMemo(() => getHelpArticlesForTab(activeTab), [activeTab]);
  const [query, setQuery] = useState('');
  const [sectionFilter, setSectionFilter] = useState('context');
  const [selectedId, setSelectedId] = useState(() => contextArticles[0]?.id || HELP_ARTICLES[0]?.id || '');
  const [activeView, setActiveView] = useState<HelpDrawerView>('assistant');

  const articles = useMemo(() => {
    const source = sectionFilter === 'context'
      ? contextArticles
      : sectionFilter === 'all'
        ? HELP_ARTICLES
        : HELP_ARTICLES.filter((article) => article.section === sectionFilter);
    return searchHelpArticles(query, source);
  }, [contextArticles, query, sectionFilter]);

  useEffect(() => {
    if (articles.length === 0) return;
    if (!articles.some((article) => article.id === selectedId)) {
      setSelectedId(articles[0].id);
    }
  }, [articles, selectedId]);

  useEffect(() => {
    if (open) {
      setActiveView('assistant');
    }
  }, [open]);

  const selectedArticle = articles.find((article) => article.id === selectedId) || articles[0];
  const currentPageLabel = activeTabLabel || formatTabLabel(activeTab);
  const sectionLabel = sectionFilter === 'context'
    ? 'This page'
    : sectionFilter === 'all'
      ? 'All topics'
      : getSectionTitle(sectionFilter);

  function handleOpenArticle(articleId: string) {
    setQuery('');
    setSectionFilter('all');
    setSelectedId(articleId);
    setActiveView('articles');
  }

  if (!open) return null;

  return (
    <>
      <button className="sd-help-overlay" onClick={onClose} aria-label="Close help" type="button" />
      <aside className="sd-help-drawer" aria-label="Help center">
        <header className="sd-help-drawer-header">
          <div className="sd-help-title-lockup">
            <span className="sd-help-mark" aria-hidden="true">
              <HelpDockIcon icon="help" />
            </span>
            <div>
              <span>TENANT GUIDANCE</span>
              <h2>Help & Support</h2>
            </div>
          </div>
          <button className="sd-help-close-button" onClick={onClose} type="button" aria-label="Close help">
            <HelpDockIcon icon="close" />
          </button>
        </header>

        <section className="sd-help-context-panel" aria-label="Current help context">
          <div>
            <span>You are on</span>
            <strong>{currentPageLabel}</strong>
            <p>{contextArticles.length} matching guide{contextArticles.length === 1 ? '' : 's'} for this workspace view.</p>
          </div>
          <div className="sd-help-context-status">
            <span aria-hidden="true" />
            Guides ready
          </div>
          <div className="sd-help-view-switch" role="group" aria-label="Help view">
            <button
              className={activeView === 'assistant' ? 'is-active' : ''}
              onClick={() => setActiveView('assistant')}
              type="button"
            >
              Hub
            </button>
            <button
              className={activeView === 'articles' ? 'is-active' : ''}
              onClick={() => setActiveView('articles')}
              type="button"
            >
              Articles
            </button>
          </div>
        </section>

        {activeView === 'articles' && (
          <div className="sd-help-tools">
            <label className="sd-help-search">
              <span>Search articles</span>
              <input
                onChange={(event) => setQuery(event.target.value)}
                placeholder="devices, alerts, branding..."
                type="search"
                value={query}
              />
            </label>
            <label className="sd-help-section-select">
              <span>Section</span>
              <select value={sectionFilter} onChange={(event) => setSectionFilter(event.target.value)}>
                <option value="context">This page</option>
                <option value="all">All topics</option>
                {HELP_SECTIONS.map((section) => (
                  <option key={section.id} value={section.id}>{section.title}</option>
                ))}
              </select>
            </label>
          </div>
        )}

        {activeView === 'assistant' ? (
          <HelpAssistant
            activeTab={activeTab}
            activeTabLabel={currentPageLabel}
            onOpenArticles={() => setActiveView('articles')}
            onOpenArticle={handleOpenArticle}
            onOpenSupport={onOpenSupport}
          />
        ) : (
          <div className="sd-help-layout">
            <nav aria-label="Help articles">
              <div className="sd-help-list-label">
                <span>{sectionLabel}</span>
                <strong>{articles.length}</strong>
              </div>
              {articles.length === 0 ? (
                <div className="sd-help-empty">No articles match this search.</div>
              ) : (
                articles.map((article) => (
                  <button
                    className={selectedArticle?.id === article.id ? 'is-active' : ''}
                    key={article.id}
                    onClick={() => setSelectedId(article.id)}
                    type="button"
                  >
                    <strong>{article.title}</strong>
                    <span>{article.summary}</span>
                  </button>
                ))
              )}
            </nav>
            {selectedArticle ? (
              <article className="sd-help-article">
                <span>{getSectionTitle(selectedArticle.section)}</span>
                <h3>{selectedArticle.title}</h3>
                <p className="sd-help-summary">{selectedArticle.summary}</p>
                <p>{selectedArticle.body}</p>
                <h4>Workflow</h4>
                <ol>
                  {selectedArticle.steps.map((step) => <li key={step}>{step}</li>)}
                </ol>
                {selectedArticle.tips && selectedArticle.tips.length > 0 && (
                  <>
                    <h4>Notes</h4>
                    <ul className="sd-help-tip-list">
                      {selectedArticle.tips.map((tip) => <li key={tip}>{tip}</li>)}
                    </ul>
                  </>
                )}
                {selectedArticle.relatedTabs && selectedArticle.relatedTabs.length > 0 && (
                  <div className="sd-help-related-list" aria-label="Related sections">
                    {selectedArticle.relatedTabs.map((tab) => <span key={tab}>{tab}</span>)}
                  </div>
                )}
              </article>
            ) : (
              <article className="sd-help-article">
                <span>NO RESULTS</span>
                <h3>No matching article</h3>
                <p>Try another keyword or switch sections.</p>
              </article>
            )}
          </div>
        )}

        <footer>
          <div>
            <strong>{contextArticles.length} contextual articles</strong>
            <span>Open the full manual for print or PDF export.</span>
          </div>
          <div className="sd-help-footer-actions">
            <a className="sd-help-manual-link" href="/help/manual" target="_blank" rel="noreferrer">
              Open Manual
            </a>
            <button onClick={onOpenSupport} type="button">Open Support</button>
          </div>
        </footer>
      </aside>
    </>
  );
}

function formatTabLabel(tab: string) {
  return tab
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

type HelpDockIconName = 'close' | 'help';

function HelpDockIcon({ icon }: { icon: HelpDockIconName }) {
  const common = {
    fill: 'none',
    stroke: 'currentColor',
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    strokeWidth: 2,
  };

  return (
    <svg viewBox="0 0 24 24" focusable="false">
      {icon === 'help' && (
        <>
          <circle {...common} cx="12" cy="12" r="8" />
          <path {...common} d="M9.8 9.5a2.4 2.4 0 0 1 4.5 1.2c0 1.6-1.4 2.1-2.1 2.9-.3.3-.4.7-.4 1.1" />
          <path {...common} d="M12 17h.01" />
        </>
      )}
      {icon === 'close' && (
        <>
          <path {...common} d="M6 6l12 12" />
          <path {...common} d="M18 6L6 18" />
        </>
      )}
    </svg>
  );
}
